/* global Backbone, _, define */
/* eslint-disable no-var, object-shorthand */
/**
 * Step derivation.
 *
 * Deciding what counts as a step is the one genuinely data-shaped job in the
 * conversational runtime, and it is the same job whichever add-ons are
 * installed: read the form content, drop what cannot be asked, and return an
 * ordered list of steps. Keeping it here rather than in the view means the
 * rules can be read — and reasoned about — without the render, focus, keyboard,
 * and theming concerns wrapped around them.
 *
 * Every function takes the data it works on and, where a step needs to point at
 * rendered markup, the view's scoped lookup. The one thing remembered between
 * calls is each field's opening value — see `baselines` — because whether a
 * value is the visitor's cannot be read off the field alone.
 */
define( 'models/steps',[], function () {
	/**
	 * The value each field opened with, keyed by model cid.
	 *
	 * An owner's Default Value setting is written into the same `value`
	 * attribute the visitor's typing lands in (core writes it during render,
	 * merge tags and all), so "does this field hold something" cannot tell a
	 * pre-filled field from an answered one. What the field held before the
	 * visitor could touch it is the missing half, and it is recorded here as
	 * each field first enters the step list.
	 */
	var baselines = {};

	/** Field types that never become a step of their own. */
	var structuralTypes = [ 'hidden', 'note', 'submit', 'hr' ];

	/**
	 * Field types that present content rather than collect an answer.
	 *
	 * Wider than `structuralTypes`, and deliberately so: an HTML statement is a
	 * step of its own — the visitor reads it and moves on — but it is not
	 * something they can answer. Ninja Forms stores such a field's markup in
	 * the same `value` attribute a text field stores typing in, so anything
	 * asking "has the visitor entered something here" has to know the
	 * difference or it will read the form's own copy back as the visitor's
	 * words.
	 */
	var displayTypes = structuralTypes.concat( [ 'html' ] );

	/** Field types that a part keeps even when conditional logic hides them. */
	var partExcludedTypes = [ 'submit', 'hidden', 'note' ];

	/**
	 * Detect the Layout & Styles row/cell shape.
	 *
	 * Duck-typed on the first item holding a `cells` collection, so the runtime
	 * does not have to depend on that add-on being installed.
	 *
	 * @param {Object} collection Candidate form content collection.
	 * @return {boolean} True when the collection is a set of layout rows.
	 */
	function collectionLooksLikeRows( collection ) {
		return Boolean(
			collection instanceof Backbone.Collection &&
				collection.length &&
				collection.first().get( 'cells' ) instanceof Backbone.Collection
		);
	}

	/**
	 * Decide whether a field can be a step of its own.
	 *
	 * @param {Object} field A field model.
	 * @return {boolean} True when the field is visible and asks something.
	 */
	function isStepField( field ) {
		return (
			Boolean( field.get( 'visible' ) ) &&
			-1 === structuralTypes.indexOf( field.get( 'type' ) )
		);
	}

	/**
	 * Flatten a layout row's cells into a single field list.
	 *
	 * @param {Object} row A layout row model.
	 * @return {Array} The row's field models, in cell order.
	 */
	function rowFields( row ) {
		var fields = [];

		row.get( 'cells' ).each( function ( cell ) {
			fields = fields.concat( cell.get( 'fields' ).models );
		} );

		return fields;
	}

	/**
	 * Extract the answerable fields from a part's content.
	 *
	 * A part's content is itself either layout rows or a flat field list, so
	 * both are handled. Only structural types are dropped here: unlike
	 * isStepField, fields hidden by conditional logic are kept, since a part is
	 * a step whether or not every field in it is showing.
	 *
	 * @param {Object} content The part's form content collection.
	 * @return {Array} Field models belonging to that part.
	 */
	function fieldsFromContent( content ) {
		var fields = [];

		if ( ! ( content instanceof Backbone.Collection ) ) {
			return fields;
		}

		if ( collectionLooksLikeRows( content ) ) {
			content.each( function ( row ) {
				fields = fields.concat( rowFields( row ) );
			} );
		} else {
			fields = content.models.slice();
		}

		return _.filter( fields, function ( field ) {
			return -1 === partExcludedTypes.indexOf( field.get( 'type' ) );
		} );
	}

	/**
	 * Build steps from a block of form content, whatever shape it is in.
	 *
	 * Used for the content of a single part, which is either layout rows or a
	 * flat field list depending on which add-ons the owner uses.
	 *
	 * @param {Object}   content A form content collection.
	 * @param {Function} lookup  The view's scoped element lookup.
	 * @return {Array} Step objects.
	 */
	function contentSteps( content, lookup ) {
		if ( ! ( content instanceof Backbone.Collection ) ) {
			return [];
		}

		return collectionLooksLikeRows( content )
			? layoutSteps( content, lookup )
			: fieldSteps( content, lookup );
	}

	/**
	 * Build one step per visible field, for plain core forms.
	 *
	 * @param {Object}   data   The field collection.
	 * @param {Function} lookup The view's scoped element lookup.
	 * @return {Array} Step objects with `key`, `fields`, and `el`.
	 */
	function fieldSteps( data, lookup ) {
		var steps = [];

		data.each( function ( field ) {
			if ( ! isStepField( field ) ) {
				return;
			}

			steps.push( {
				key: field.get( 'key' ),
				fields: rememberBaselines( [ field ] ),
				el: lookup(
					'#nf-field-' + field.get( 'id' ) + '-container'
				).get( 0 ),
			} );
		} );

		return steps;
	}

	/**
	 * Build one step per layout row that holds a real question.
	 *
	 * Rows of only structural fields — a divider, a note, the submit button —
	 * are skipped rather than shown as empty steps. Row order in the data
	 * matches the rendered `.nf-row` order, which is how each step finds its
	 * element.
	 *
	 * @param {Object}   data   The row collection.
	 * @param {Function} lookup The view's scoped element lookup.
	 * @return {Array} Step objects with `key`, `fields`, and `el`.
	 */
	function layoutSteps( data, lookup ) {
		var steps = [];
		var rowElements = lookup( '.nf-row' );

		data.each( function ( row, index ) {
			var meaningful = _.filter( rowFields( row ), isStepField );

			if ( ! meaningful.length ) {
				return;
			}

			steps.push( {
				key: row.cid,
				fields: rememberBaselines( meaningful ),
				el: rowElements.get( index ),
			} );
		} );

		return steps;
	}

	/**
	 * Build the step for one multi-part page.
	 *
	 * Multi-part renders only the current part, so every part step points at
	 * the same body element; the add-on swaps its contents.
	 *
	 * @param {Object}   part   A visible part model.
	 * @param {Function} lookup The view's scoped element lookup.
	 * @return {Object} Step object with `key`, `fields`, `model`, `el`.
	 */
	function partStep( part, lookup ) {
		return {
			key: part.get( 'key' ) || part.cid,
			fields: rememberBaselines(
				fieldsFromContent( part.get( 'formContentData' ) )
			),
			model: part,
			el: lookup( '.nf-mp-body' ).get( 0 ),
		};
	}

	/**
	 * Whether a field can hold something the visitor entered.
	 *
	 * @param {Object} field A field model.
	 * @return {boolean} True when the field collects an answer.
	 */
	function collectsAnswer( field ) {
		return -1 === displayTypes.indexOf( field.get( 'type' ) );
	}

	/**
	 * A field's value as a comparable string.
	 *
	 * @param {Object} field A field model.
	 * @return {string} The trimmed value.
	 */
	function fieldValue( field ) {
		return String( field.get( 'value' ) || '' ).trim();
	}

	/**
	 * Record what a field opened with, the first time it is seen.
	 *
	 * Only the first sighting counts: the step list is rebuilt on every
	 * conditional-logic change, and re-reading the value then would adopt the
	 * visitor's own answers as the baseline they are measured against.
	 *
	 * @param {Array} fields The fields of a step.
	 * @return {Array} The same fields, for chaining.
	 */
	function rememberBaselines( fields ) {
		_.each( fields, function ( field ) {
			if ( ! _.has( baselines, field.cid ) ) {
				baselines[ field.cid ] = fieldValue( field );
			}
		} );

		return fields;
	}

	/**
	 * Whether a field holds something the visitor put there.
	 *
	 * Three things are not the visitor's: a field that collects no answer at
	 * all (a statement's markup lives in `value` too), a field still holding
	 * the value it opened with, and an empty one. A field whose baseline was
	 * never recorded falls back to "anything non-empty counts", which errs
	 * towards asking before discarding rather than discarding silently.
	 *
	 * @param {Object} field A field model.
	 * @return {boolean} True when the value is the visitor's own.
	 */
	function hasVisitorValue( field ) {
		if ( ! collectsAnswer( field ) ) {
			return false;
		}

		var value = fieldValue( field );

		return '' !== value && value !== baselines[ field.cid ];
	}

	return {
		structuralTypes: structuralTypes,
		displayTypes: displayTypes,
		collectsAnswer: collectsAnswer,
		hasVisitorValue: hasVisitorValue,
		rememberBaselines: rememberBaselines,
		collectionLooksLikeRows: collectionLooksLikeRows,
		contentSteps: contentSteps,
		isStepField: isStepField,
		rowFields: rowFields,
		fieldsFromContent: fieldsFromContent,
		fieldSteps: fieldSteps,
		layoutSteps: layoutSteps,
		partStep: partStep,
	};
} );

